import React, { useState } from 'react';
import { Button } from '../../atoms/Button';
import { Icon } from '@lnr-v2/ui';
import { useTermsByTaxonomy } from '../../../hooks';
import type { TaxonomyWithTerms } from '../../../hooks/useTaxonomies';
import type { TermWithTaxonomy } from '../../../hooks/useTerms';
import styles from './TaxonomyCard.module.css';

interface TaxonomyCardProps {
  taxonomy: TaxonomyWithTerms;
  onEditTaxonomy: (taxonomy: TaxonomyWithTerms) => void;
  onDeleteTaxonomy: (taxonomyId: string) => void;
  onCreateTerm: (taxonomy: TaxonomyWithTerms) => void;
  onEditTerm: (term: TermWithTaxonomy, taxonomy: TaxonomyWithTerms) => void;
  onDeleteTerm: (termId: string) => void;
}

const TaxonomyCardComponent: React.FC<TaxonomyCardProps> = ({
  taxonomy,
  onEditTaxonomy,
  onDeleteTaxonomy,
  onCreateTerm,
  onEditTerm,
  onDeleteTerm,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Lazy load terms only when expanded
  const termsQuery = useTermsByTaxonomy(taxonomy.id, {
    useId: true,
    enabled: isExpanded,
  });

  const terms = termsQuery.data || [];
  const termsLoading = termsQuery.isLoading;
  const termsError = termsQuery.error;

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={styles.taxonomyCard}>
      {/* Taxonomy Header */}
      <div className={styles.taxonomyHeader}>
        <button
          className={styles.expandButton}
          onClick={handleToggleExpand}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
        >
          <Icon
            name="chevron-right"
            size="sm"
            className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}
          />
        </button>

        <div className={styles.taxonomyInfo}>
          <h3 className={styles.taxonomyName}>{taxonomy.name}</h3>
          <span className={styles.taxonomySlug}>{taxonomy.slug}</span>
          {taxonomy.description && (
            <p className={styles.taxonomyDescription}>{taxonomy.description}</p>
          )}
        </div>

        <div className={styles.taxonomyActions}>
          <span className={styles.termCount}>
            {taxonomy.termCount || 0} terms
          </span>
          <Button
            onClick={() => onCreateTerm(taxonomy)}
            variant="ghost"
            size="sm"
          >
            <Icon name="plus" size="sm" />
            Add Term
          </Button>
          <Button
            onClick={() => onEditTaxonomy(taxonomy)}
            variant="ghost"
            size="sm"
          >
            <Icon name="edit" size="sm" />
          </Button>
          <Button
            onClick={() => onDeleteTaxonomy(taxonomy.id)}
            variant="ghost"
            size="sm"
          >
            <Icon name="trash" size="sm" />
          </Button>
        </div>
      </div>

      {/* Terms Section (Lazy Loaded) */}
      {isExpanded && (
        <div className={styles.termsSection}>
          {termsLoading ? (
            <div className={styles.termsLoading}>
              <div className={styles.spinner} />
              <span>Loading terms...</span>
            </div>
          ) : termsError ? (
            <div className={styles.termsError}>
              <Icon name="x-circle" size="sm" />
              <span>Failed to load terms</span>
            </div>
          ) : terms.length > 0 ? (
            <div className={styles.termsGrid}>
              {terms.map((term: TermWithTaxonomy) => (
                <div key={term.id} className={styles.termCard}>
                  <div className={styles.termHeader}>
                    {term.icon && <Icon name={term.icon} size="sm" />}
                    <div className={styles.termInfo}>
                      <span className={styles.termName}>{term.nameEN}</span>
                      {term.nameFR && (
                        <span className={styles.termNameFR}>{term.nameFR}</span>
                      )}
                    </div>
                    {term.color && (
                      <div
                        className={styles.termColor}
                        style={{ backgroundColor: term.color }}
                      />
                    )}
                  </div>
                  {term.usageCount !== undefined && (
                    <span className={styles.usageCount}>
                      Used {term.usageCount} times
                    </span>
                  )}
                  <div className={styles.termActions}>
                    <Button
                      onClick={() => onEditTerm(term, taxonomy)}
                      variant="ghost"
                      size="sm"
                    >
                      <Icon name="edit" size="xs" />
                    </Button>
                    <Button
                      onClick={() => onDeleteTerm(term.id)}
                      variant="ghost"
                      size="sm"
                    >
                      <Icon name="trash" size="xs" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noTerms}>
              <p>No terms yet. Add your first term to this taxonomy.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Memoized TaxonomyCard to prevent unnecessary re-renders
 * Only re-renders when taxonomy data or termCount changes
 */
export const TaxonomyCard = React.memo(
  TaxonomyCardComponent,
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if these values change
    return (
      prevProps.taxonomy.id === nextProps.taxonomy.id &&
      prevProps.taxonomy.termCount === nextProps.taxonomy.termCount &&
      prevProps.taxonomy.updatedAt === nextProps.taxonomy.updatedAt
    );
  }
);
